import React from 'react'

// Recovers from lazy-chunk load failures introduced by route code-splitting.
// The common trigger: a new build is deployed while a user has an old tab (or a
// cached/stale index.html), so the old chunk filenames it asks for now 404 and
// React.lazy throws a "ChunkLoadError". Without a boundary that error has no
// recovery and the screen just freezes on the <Suspense> spinner.
//
// On a chunk error we force one reload (which fetches a fresh index.html and the
// new chunk names). A short time-window guard prevents a reload loop if the
// chunk is genuinely, persistently unavailable — after that we show a manual
// retry instead of looping.
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { failed: false }
  }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error) {
    const message = String((error && (error.message || error.name)) || '')
    const isChunkError = /Loading chunk|ChunkLoadError|Loading CSS chunk|dynamically imported module/i.test(message)
    if (!isChunkError) return

    // Only auto-reload if we didn't already just try (avoids an infinite loop).
    const last = Number(sessionStorage.getItem('chunkReloadAt') || 0)
    const now = Date.now()
    if (now - last > 10000) {
      sessionStorage.setItem('chunkReloadAt', String(now))
      window.location.reload()
    }
  }

  render() {
    if (this.state.failed) {
      return (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <h4 style={{ marginBottom: '1rem' }}>This page failed to load.</h4>
          <p className="text-muted">A new version may have just been released.</p>
          <button
            type="button"
            className="btn btn-secondary fw-bold"
            onClick={() => {
              sessionStorage.removeItem('chunkReloadAt')
              window.location.reload()
            }}
          >
            Reload
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

export default ChunkErrorBoundary
